import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import clsx from "clsx";

import { makeRequest } from "../../utils/request";
import { Button, EmptyState, Spinner, Text, View } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { FineTuningPipelineStepper } from "../../components/FineTuningPipelineStepper";
import { FineTuningMutationNotice, useMutationNotice } from "../../components/FineTuningMutationNotice";

// A released, agent-callable model (voicerun/<name>) pinned to an evaluated fine-tune. Each org is
// served in a single cluster, so `endpoints` (cluster name -> in-cluster serving URL) has one entry;
// it's keyed by cluster because different orgs live in different clusters.
type RegisteredModel = {
  id: string;
  name: string;
  status: string;
  org_id: string;
  base_model: string;
  model_uri: string;
  job_id: string | null;
  eval_id: string | null;
  deployment_id: string | null;
  endpoints: Record<string, string>;
  // "text" | "asr". ASR models are served on their own STT endpoint (off the completions proxy) and
  // are excluded from the chat model->URI JWT, so we mark them with an "ASR" chip. Optional: older
  // API responses may omit it, in which case we treat the model as text.
  modality?: string;
  // "lora" | "qlora" | "full" — HOW it was trained. Null for a raw model_uri release (e.g. ASR) that
  // had no fine-tune method. Shown as a chip so full-FT models are distinguishable from LoRA at a glance.
  method?: string | null;
  // Backend-derived runtime serving health: "serving" | "provisioning" | "orphaned" | "retired".
  // Distinct from `status` (active/retired lifecycle) — it's the single source of truth for the badge.
  serving_state?: string;
  description: string | null;
  created_at: string;
  updated_at: string;
};

type ModelEvent = {
  id: string;
  type: string;
  level: string;
  message: string;
  created_at: string;
};

const POLL_MS = 6000;
const ALL_ORGS = "all_orgs=true";

const COLUMNS = "1fr 1.4fr 1.4fr 1.5fr 0.7fr 0.8fr 0.9fr";

const asUtc = (iso: string) => (/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);

const relativeTime = (iso: string | null): string => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(asUtc(iso)).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const clusters = (m: RegisteredModel) => Object.keys(m.endpoints || {});

const STATE_STYLES: Record<string, { title: string; cls: string }> = {
  serving: { title: "live — callable at its endpoint(s)", cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" },
  provisioning: {
    title: "registered; serving pod is still coming up — not callable yet",
    cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  },
  orphaned: {
    title: "active but its backing deployment is terminated/gone — nothing serving; retire it or redeploy",
    cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  },
  retired: { title: "retired — no longer callable", cls: "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300" },
};

// Serving-readiness for the badge. Prefer the backend's derived `serving_state` — it alone
// distinguishes "provisioning" (backing deployment still coming up) from "orphaned" (backing
// deployment terminated/gone, so it can NEVER serve until redeployed; this previously rendered as
// "provisioning" forever). Fall back to a status+endpoints heuristic for older API responses that
// don't yet include serving_state.
const servingState = (m: RegisteredModel): { label: string; title: string; cls: string } => {
  const state = m.serving_state ?? (m.status !== "active" ? "retired" : Object.keys(m.endpoints || {}).length ? "serving" : "provisioning");
  return { label: state, ...(STATE_STYLES[state] ?? STATE_STYLES.provisioning) };
};

const StatusBadge = ({ model }: { model: RegisteredModel }) => {
  const s = servingState(model);
  return (
    <span
      title={s.title}
      className={clsx("inline-block rounded-full px-2 py-0.5 text-xs font-medium", s.cls)}
    >
      {s.label}
    </span>
  );
};

export const AdminFineTuningModelsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedName = searchParams.get("name");

  const [models, setModels] = useState<RegisteredModel[]>([]);
  // GPU per model, pulled from its backing deployment (the registry doesn't store the accelerator).
  const [gpuByModel, setGpuByModel] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setIsLoading(true);
    try {
      type Dep = { org_id: string; served_model_name: string; accelerator: string; status: string };
      const [modelsRes, depsRes] = await Promise.all([
        makeRequest<{ data: RegisteredModel[] }>(`/v1/fine_tuning/models?${ALL_ORGS}&limit=200`),
        makeRequest<{ data: Dep[] }>(`/v1/fine_tuning/deployments?${ALL_ORGS}&limit=200`).catch(() => ({
          data: [] as Dep[],
        })),
      ]);
      setModels(modelsRes.data);
      // map (org|servedName) -> accelerator, preferring the running deployment
      const gpu: Record<string, string> = {};
      for (const d of depsRes.data) {
        const key = `${d.org_id}|${d.served_model_name}`;
        if (d.status === "running" || !(key in gpu)) gpu[key] = d.accelerator;
      }
      setGpuByModel(gpu);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load models");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
  }, [load]);

  // endpoints fill in as serving comes up in each cluster — keep polling while any model is active
  const hasActive = models.some((m) => m.status === "active");
  useEffect(() => {
    if (!hasActive) return;
    const t = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(t);
  }, [hasActive, load]);

  const selected = models.find((m) => m.name === selectedName) || null;
  const gpuFor = (m: RegisteredModel) => gpuByModel[`${m.org_id}|${m.name}`] ?? "—";

  return (
    <View className="h-full w-full bg-gray-50 dark:bg-slate-900">
      <div className={clsx("h-full w-full relative transition-all duration-300", selectedName && "pr-[440px]")}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Text className="text-xl font-semibold">Fine-Tuning Releases</Text>
            <Text className="text-sm text-gray-500 dark:text-gray-400">
              {models.length} {models.length === 1 ? "release" : "releases"} · all orgs
            </Text>
          </div>
          <Button
            variant="ghost"
            type="button"
            onClick={() => load(true)}
          >
            <Icon
              name="refresh"
              size={18}
              className="text-brand"
            />
          </Button>
        </div>

        <FineTuningPipelineStepper />

        <div
          className="grid border-b-2 border-gray-200 dark:border-gray-700 px-6"
          style={{ gridTemplateColumns: COLUMNS }}
        >
          {["Org", "Model", "Base model", "Cluster", "GPU", "Status", "Released"].map((h) => (
            <span
              key={h}
              className="py-3 pr-2 text-sm font-semibold text-gray-700 dark:text-gray-200"
            >
              {h}
            </span>
          ))}
        </div>

        <div
          className="overflow-y-auto"
          style={{ height: "calc(100% - 110px)" }}
        >
          {isLoading ? (
            <View className="flex items-center justify-center py-20">
              <Spinner className="!text-brand" />
            </View>
          ) : error ? (
            <View className="flex items-center justify-center py-20">
              <Text className="text-error">{error}</Text>
            </View>
          ) : models.length === 0 ? (
            <EmptyState
              icon="rocket"
              title="No releases yet"
              description="Releases created via `vr ft release` become agent-callable as voicerun/<name> and appear here, across all organizations."
            />
          ) : (
            models.map((m) => {
              const served = clusters(m);
              return (
                <div
                  key={m.id}
                  onClick={() => setSearchParams({ name: m.name })}
                  className={clsx(
                    "grid items-center px-6 border-b border-gray-100 dark:border-gray-800 cursor-pointer",
                    "hover:bg-gray-100 dark:hover:bg-gray-800",
                    selectedName === m.name && "bg-gray-100 dark:bg-gray-800",
                  )}
                  style={{ gridTemplateColumns: COLUMNS }}
                >
                  <span
                    className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400"
                    title={m.org_id}
                  >
                    {m.org_id}
                  </span>
                  <span
                    className="py-3 pr-2 flex items-center gap-1.5 min-w-0 text-sm font-medium"
                    title={`voicerun/${m.name}`}
                  >
                    <span className="truncate">{m.name}</span>
                    {m.modality && m.modality !== "text" && (
                      <span
                        className="flex-shrink-0 rounded bg-info/10 px-1.5 py-0.5 text-[10px] font-medium uppercase text-info"
                        title="Served on a dedicated endpoint, off the completions proxy — not used for chat completions"
                      >
                        {m.modality}
                      </span>
                    )}
                    {m.method && (
                      <span
                        className={clsx(
                          "flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase",
                          m.method === "full" ? "bg-warning/10 text-warning" : "bg-gray-500/10 text-gray-500 dark:text-gray-400",
                        )}
                        title={`Fine-tuned with ${m.method.toUpperCase()}`}
                      >
                        {m.method}
                      </span>
                    )}
                  </span>
                  <span
                    className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400"
                    title={m.base_model}
                  >
                    {m.base_model}
                  </span>
                  <span
                    className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400"
                    title={served.join(", ")}
                  >
                    {served.length ? served.join(", ") : "—"}
                  </span>
                  <span
                    className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400"
                    title={gpuFor(m)}
                  >
                    {gpuFor(m)}
                  </span>
                  <span className="py-3 pr-2">
                    <StatusBadge model={m} />
                  </span>
                  <span className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400">{relativeTime(m.created_at)}</span>
                </div>
              );
            })
          )}
        </div>
      </div>

      {selected && (
        <ModelDetailPanel
          model={selected}
          gpu={gpuFor(selected)}
          onClose={() => setSearchParams({})}
          onChanged={() => load(false)}
        />
      )}
    </View>
  );
};

type PanelProps = {
  model: RegisteredModel;
  gpu: string;
  onClose: () => void;
  onChanged: () => void;
};

const ModelDetailPanel = ({ model, gpu, onClose, onChanged }: PanelProps) => {
  const [events, setEvents] = useState<ModelEvent[]>([]);
  const [isRetiring, setIsRetiring] = useState(false);
  const { notice, showError, showSuccess, clearNotice } = useMutationNotice();
  const nameRef = useRef(model.name);
  nameRef.current = model.name;

  // a notice belongs to the model it was shown for — drop it when switching rows
  useEffect(() => clearNotice(), [model.name, clearNotice]);

  const isRetired = model.status !== "active";
  const served = Object.entries(model.endpoints || {});

  const loadEvents = useCallback(async () => {
    try {
      const { data } = await makeRequest<{ data: ModelEvent[] }>(`/v1/fine_tuning/models/${encodeURIComponent(model.name)}/events?all_orgs=true`);
      if (nameRef.current === model.name) setEvents(data);
    } catch {
      /* best-effort */
    }
  }, [model.name]);

  useEffect(() => {
    setEvents([]);
    loadEvents();
    if (isRetired) return;
    const t = setInterval(loadEvents, POLL_MS);
    return () => clearInterval(t);
  }, [loadEvents, isRetired]);

  const onRetire = async () => {
    setIsRetiring(true);
    clearNotice();
    try {
      await makeRequest(`/v1/fine_tuning/models/${encodeURIComponent(model.name)}?all_orgs=true`, { method: "DELETE" });
      showSuccess("Retirement requested.");
      onChanged();
      loadEvents();
    } catch (e) {
      showError(e, "Failed to retire the model");
    } finally {
      setIsRetiring(false);
    }
  };

  return (
    <div className="fixed right-0 top-0 h-screen w-[440px] bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col z-20">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Text className="text-base font-semibold">Model</Text>
          <StatusBadge model={model} />
        </div>
        <Button
          variant="ghost"
          type="button"
          onClick={onClose}
        >
          <Icon
            name="close"
            size={18}
            className="text-gray-500"
          />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        <Section
          label="Agent-callable name"
          value={`voicerun/${model.name}`}
          mono
        />
        <Section
          label="Organization"
          value={model.org_id}
          mono
        />
        <Field
          label="Base model"
          value={model.base_model || "—"}
        />
        {model.modality && model.modality !== "text" && (
          <Field
            label="Modality"
            value={`${model.modality.toUpperCase()} — served off the completions proxy`}
          />
        )}
        {model.method && (
          <Field
            label="Method"
            value={model.method.toUpperCase()}
          />
        )}
        <Field
          label="GPU"
          value={gpu}
        />

        <div>
          <Text className="block text-xs uppercase text-gray-400 mb-1">Cluster</Text>
          {served.length === 0 ? (
            <Text className="text-sm text-gray-400">
              {servingState(model).label === "orphaned"
                ? "Backing deployment is terminated/gone — not serving. Retire it or redeploy."
                : "Not serving in any cluster yet."}
            </Text>
          ) : (
            <div className="space-y-2">
              {served.map(([cluster, url]) => (
                <div key={cluster}>
                  <Text className="block text-sm font-medium break-all">{cluster}</Text>
                  <Text className="block font-mono text-xs text-gray-500 dark:text-gray-400 break-all">{url}</Text>
                </div>
              ))}
            </div>
          )}
        </div>

        <Section
          label="Model URI"
          value={model.model_uri || "—"}
          mono
        />
        {model.job_id && (
          <Field
            label="Source job"
            value={model.job_id}
            mono
          />
        )}
        {model.eval_id && (
          <Field
            label="Eval"
            value={model.eval_id}
            mono
          />
        )}
        {model.deployment_id && (
          <div className="flex items-baseline justify-between gap-3">
            <Text className="text-xs uppercase text-gray-400 flex-shrink-0">Deployment</Text>
            <RouterLink
              to={`/admin/fine-tuning-deployments?id=${model.deployment_id}`}
              className="min-w-0 text-right"
              title="Open this deployment"
            >
              <Text className="break-all font-mono text-xs text-brand hover:underline">{model.deployment_id}</Text>
            </RouterLink>
          </div>
        )}
        {model.description && (
          <Section
            label="Description"
            value={model.description}
          />
        )}
        <Field
          label="Released"
          value={new Date(asUtc(model.created_at)).toLocaleString()}
        />
        <Field
          label="Updated"
          value={new Date(asUtc(model.updated_at)).toLocaleString()}
        />

        <div>
          <Text className="text-xs uppercase text-gray-400 mb-2">Events</Text>
          {events.length === 0 ? (
            <Text className="text-sm text-gray-400">No events yet.</Text>
          ) : (
            <div className="space-y-2">
              {events.map((e) => (
                <div
                  key={e.id}
                  className="flex gap-2 text-sm"
                >
                  <span
                    className={clsx(
                      "mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0",
                      e.level === "error" ? "bg-error" : e.level === "warning" ? "bg-yellow-500" : "bg-info",
                    )}
                  />
                  <div className="min-w-0">
                    <Text className="block text-gray-800 dark:text-gray-200 break-words">{e.message}</Text>
                    <Text className="block text-xs text-gray-400 mt-0.5">{relativeTime(e.created_at)}</Text>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* outside the retired guard so a success notice survives the status flipping to retired */}
      <FineTuningMutationNotice
        notice={notice}
        className="px-4 py-3"
      />

      {!isRetired && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            className="w-full"
            variant="ghost"
            type="button"
            onClick={onRetire}
            isLoading={isRetiring}
          >
            <Text className="text-error">Retire model</Text>
          </Button>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div className="flex items-baseline justify-between gap-3">
    <Text className="text-xs uppercase text-gray-400 flex-shrink-0">{label}</Text>
    <Text className={clsx("text-sm text-right break-words", mono && "font-mono text-xs")}>{value}</Text>
  </div>
);

const Section = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div>
    {/* the design-system Text is inline; `block` keeps the value on its own line under the label */}
    <Text className="block text-xs uppercase text-gray-400 mb-1">{label}</Text>
    <Text className={clsx("block text-sm break-all", mono && "font-mono text-xs")}>{value}</Text>
  </div>
);
